export interface PaymentMethodDiscountSettings {
  achCheckDiscountEnabled?: boolean;
  achCheckDiscountPercent?: number;
}

/**
 * Direct discount from final principal, after tax and document discounts.
 * Keep gratuity outside grossCents and add it to chargeCents afterwards.
 * Mirrors the server's integer-cent, half-up calculation for checkout previews.
 */
export function computePaymentMethodDiscount(
  settings: PaymentMethodDiscountSettings | null | undefined,
  grossCents: number,
  method: string,
): {
  enabled: boolean;
  percent: number;
  grossCents: number;
  discountCents: number;
  chargeCents: number;
} {
  if (!Number.isSafeInteger(grossCents) || grossCents < 0) {
    throw new RangeError('grossCents must be a nonnegative safe integer');
  }

  const configuredPercent = settings?.achCheckDiscountPercent;
  const normalizedMethod = method.toUpperCase();
  const enabled =
    settings?.achCheckDiscountEnabled === true &&
    (normalizedMethod === 'ACH' || normalizedMethod === 'CHECK') &&
    typeof configuredPercent === 'number' &&
    Number.isFinite(configuredPercent) &&
    configuredPercent > 0 &&
    configuredPercent < 100;
  const percent = enabled ? configuredPercent! : 0;
  let discountCents = 0;
  if (enabled) {
    const [coefficient = '', exponent = '0'] = percent.toString().split('e');
    const [whole = '', fraction = ''] = coefficient.split('.');
    const numerator = BigInt(whole + fraction);
    const denominator = 10n ** BigInt(fraction.length + 2 - Number(exponent));
    discountCents = Number(
      (BigInt(grossCents) * numerator * 2n + denominator) / (denominator * 2n),
    );
  }

  return { enabled, percent, grossCents, discountCents, chargeCents: grossCents - discountCents };
}

/** Legacy surcharge presentation helpers for tenants without the direct discount. */
export const CARD_PRICE_UPLIFT_CAP_PERCENT = 3;

export function surchargeToDiscountPercent(surchargePercent: number): number {
  if (!Number.isFinite(surchargePercent) || surchargePercent <= 0) return 0;
  const cappedPercent = Math.min(surchargePercent, CARD_PRICE_UPLIFT_CAP_PERCENT);
  return (cappedPercent / (100 + cappedPercent)) * 100;
}

/** Convert the configured ACH/check discount back to the legacy stored uplift. */
export function discountToSurchargePercent(discountPercent: number): number {
  if (!Number.isFinite(discountPercent) || discountPercent <= 0 || discountPercent >= 100) return 0;
  return (discountPercent / (100 - discountPercent)) * 100;
}

/** Maximum discount the two-decimal settings field can represent. */
export const MAX_EDITABLE_PAYMENT_METHOD_DISCOUNT_PERCENT =
  Math.floor(surchargeToDiscountPercent(CARD_PRICE_UPLIFT_CAP_PERCENT) * 100) / 100;

/**
 * Convert the two-decimal settings value back to the legacy stored uplift.
 * The displayed maximum (2.91%) represents the exact 3% processor ceiling,
 * so snap that boundary back to 3% instead of persisting 2.997219...%.
 */
export function editableDiscountToSurchargePercent(discountPercent: number): number {
  if (discountPercent >= MAX_EDITABLE_PAYMENT_METHOD_DISCOUNT_PERCENT) {
    return CARD_PRICE_UPLIFT_CAP_PERCENT;
  }
  return discountToSurchargePercent(discountPercent);
}

export function effectivePaymentMethodDiscountPercent(
  achOrCheckPrice: number,
  cardPriceDifference: number,
  fallbackSurchargePercent = 0,
): number {
  const cardPrice = achOrCheckPrice + cardPriceDifference;
  if (
    Number.isFinite(cardPrice) &&
    cardPrice > 0 &&
    Number.isFinite(cardPriceDifference) &&
    cardPriceDifference >= 0
  ) {
    return (cardPriceDifference / cardPrice) * 100;
  }
  return surchargeToDiscountPercent(fallbackSurchargePercent);
}

export function formatDiscountPercent(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0%';
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(2)}%`;
}
